import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { getListingMapsInfo } from "../utils/listing-address";
import { withParkingPhotoWidth } from "../utils/parking-photo-url";
import useBreakpoint from "../utils/use-breakpoint";

const PARKING_PHOTO_WIDTH_MOBILE = 420;
const PARKING_PHOTO_WIDTH_DESKTOP_MODAL = 475;
const PARKING_PHOTO_WIDTH_DESKTOP_CARD = 500;

export function listingHasEntryParkingInfo(listing: ApiListing): boolean {
  const { mapsUrl } = getListingMapsInfo(listing);
  return Boolean(
    mapsUrl ||
      listing.parking_designation ||
      listing.garage_code ||
      listing.entry_instructions ||
      listing.parking_photo_url,
  );
}

function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <label className="text-xs sm:text-sm text-gray-600 dark:text-gray-300">
      {children}
    </label>
  );
}

function AddressBlock({
  mapsUrl,
  lines,
  label,
}: {
  mapsUrl: string;
  lines: string[];
  label: string;
}) {
  return (
    <div className="flex flex-col">
      <FieldLabel>{label}</FieldLabel>
      <a
        href={mapsUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="underline text-sm sm:text-base leading-6 text-primary-main hover:text-secondary-main cursor-pointer"
      >
        {lines.length > 0 ? (
          lines.map((line, index) => (
            <span key={`address-line-${index}`} className="block">
              {line}
            </span>
          ))
        ) : (
          "Google Maps"
        )}
      </a>
    </div>
  );
}

function ParkingPhoto({
  url,
  label,
  width,
  className = "flex flex-col min-w-0",
}: {
  url: string;
  label: string;
  width: number;
  className?: string;
}) {
  return (
    <div className={className}>
      <FieldLabel>{label}</FieldLabel>
      <img
        src={withParkingPhotoWidth(url, width)}
        alt={label}
        className="max-h-64 w-full rounded-md object-cover"
      />
    </div>
  );
}

/** Shared Entry & Parking fields + photo layout (listing card and task modal). */
export const ListingEntryParkingContent: React.FC<{
  listing: ApiListing;
  /**
   * `card` — desktop (md+): text left, parking photo right; mobile: stacked.
   * `modal` — photo full-width at bottom (default).
   */
  layout?: "card" | "modal";
  /** Override for location tasks (driving directions) vs listing entry instructions. */
  entryInstructionsLabel?: string;
}> = ({ listing, layout = "modal", entryInstructionsLabel }) => {
  const { t } = useTranslation();
  const breakpoint = useBreakpoint();
  const isMobile = ["xs", "sm", "md"].includes(breakpoint);
  const photoWidth = isMobile
    ? PARKING_PHOTO_WIDTH_MOBILE
    : layout === "card"
      ? PARKING_PHOTO_WIDTH_DESKTOP_CARD
      : PARKING_PHOTO_WIDTH_DESKTOP_MODAL;
  const { lines, mapsUrl } = useMemo(
    () => getListingMapsInfo(listing),
    [listing],
  );

  const addressLabel = t("listings_card_label_address");
  const entryLabel =
    entryInstructionsLabel ?? t("listings_card_label_entry_instructions");
  const designationLabel = t("listings_card_label_parking_designation");
  const garageLabel = t("listings_card_label_garage_code");
  const photoLabel = t("listings_card_label_parking_photo");

  const textFields = (
    <>
      {mapsUrl && (
        <AddressBlock mapsUrl={mapsUrl} lines={lines} label={addressLabel} />
      )}
      {listing.entry_instructions && (
        <div className="flex flex-col">
          <FieldLabel>{entryLabel}</FieldLabel>
          <p className="text-sm sm:text-base leading-6 text-gray-900 dark:text-white whitespace-pre-wrap">
            {listing.entry_instructions}
          </p>
        </div>
      )}
      {listing.parking_designation && (
        <div className="flex flex-col min-w-0">
          <FieldLabel>{designationLabel}</FieldLabel>
          <p className="text-sm sm:text-base leading-6 text-gray-900 dark:text-white whitespace-pre-wrap">
            {listing.parking_designation}
          </p>
        </div>
      )}
      {listing.garage_code && (
        <div className="flex flex-col min-w-0">
          <FieldLabel>{garageLabel}</FieldLabel>
          <p className="text-sm sm:text-base leading-6 text-gray-900 dark:text-white">
            {listing.garage_code}
          </p>
        </div>
      )}
    </>
  );

  if (layout === "card") {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="flex flex-col gap-3 min-w-0">{textFields}</div>
        {listing.parking_photo_url && (
          <ParkingPhoto
            url={listing.parking_photo_url}
            label={photoLabel}
            width={photoWidth}
          />
        )}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      {mapsUrl && (
        <div className="md:col-span-2">
          <AddressBlock mapsUrl={mapsUrl} lines={lines} label={addressLabel} />
        </div>
      )}
      {listing.entry_instructions && (
        <div className="flex flex-col md:col-span-2">
          <FieldLabel>{entryLabel}</FieldLabel>
          <p className="text-sm sm:text-base leading-6 text-gray-900 dark:text-white whitespace-pre-wrap">
            {listing.entry_instructions}
          </p>
        </div>
      )}
      {listing.parking_designation && (
        <div className="flex flex-col min-w-0 md:col-span-2">
          <FieldLabel>{designationLabel}</FieldLabel>
          <p className="text-sm sm:text-base leading-6 text-gray-900 dark:text-white whitespace-pre-wrap">
            {listing.parking_designation}
          </p>
        </div>
      )}
      {listing.garage_code && (
        <div className="flex flex-col min-w-0 md:col-span-2">
          <FieldLabel>{garageLabel}</FieldLabel>
          <p className="text-sm sm:text-base leading-6 text-gray-900 dark:text-white">
            {listing.garage_code}
          </p>
        </div>
      )}
      {listing.parking_photo_url && (
        <ParkingPhoto
          url={listing.parking_photo_url}
          label={photoLabel}
          width={photoWidth}
          className="flex flex-col min-w-0 md:col-span-2"
        />
      )}
    </div>
  );
};
